import {
  Autocomplete,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  OrganizationManager as OrganizationManagerEntity,
  type OrganizationManager,
  type VolunteerOrgActivityDomain,
} from "@entities";
import {
  organizationManagerRepo,
  volunteerOrgActivityDomainRepo,
} from "@repositories";
import { normalizePhoneDigitsForSearch } from "@shared/helpers/normalize-phone-for-search";
import {
  VOLUNTEER_ORG_EVENT_TYPES,
  newVolunteerOrgContactDraft,
  type VolunteerOrgContactDraft,
  type VolunteerOrgEventFlags,
} from "@shared/helpers/volunteer-organization";
import { hasValidCoordinates } from "@shared/helpers/geo";
import { FormGoogle } from "../../../components/form-components/form-google-autocomplete/FormGoogle";
import { useAlert } from "../../../contexts/AlertProvider";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import {
  ChoiceTile,
  FormSection,
  quietButtonSx,
} from "./volunteer-org-form-ui";

export type VolunteerOrgLocationValue = {
  address: string;
  coordinates: { lat: number; lng: number } | null;
};

export function VolunteerOrgHandlingManagerField({
  value,
  onChange,
  helperText,
}: {
  value: OrganizationManager | null;
  onChange: (manager: OrganizationManager | null) => void;
  helperText?: string;
}) {
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<OrganizationManager[]>(
    value ? [value] : [],
  );

  useEffect(() => {
    if (!value) return;
    setOptions((prev) =>
      prev.some((manager) => manager.id === value.id) ? prev : [value, ...prev],
    );
  }, [value]);

  const searchManagers = useCallback(async (query: string) => {
    const q = query.trim();
    if (q.length < 2) {
      setOptions(value ? [value] : []);
      return;
    }
    const phoneQuery = normalizePhoneDigitsForSearch(q);
    const list = await organizationManagerRepo.find({
      where: OrganizationManagerEntity.hyperFilter({
        $or: [
          { name: { $contains: q } },
          { phoneNumber: { $contains: phoneQuery } },
        ],
      }),
      include: { organization: true },
      orderBy: { name: "asc" },
      limit: 25,
    });
    setOptions(list);
  }, [value]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void searchManagers(search);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [search, searchManagers]);

  return (
    <Autocomplete
      options={
        value && !options.some((manager) => manager.id === value.id)
          ? [value, ...options]
          : options
      }
      value={value}
      onChange={(_, manager) => onChange(manager)}
      inputValue={search}
      onInputChange={(_, next) => setSearch(next)}
      getOptionLabel={(manager) =>
        `${manager.name}${manager.organization?.name ? ` · ${manager.organization.name}` : ""}`
      }
      isOptionEqualToValue={(a, b) => a.id === b.id}
      noOptionsText="הקלד לפחות 2 תווים לחיפוש"
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="מנהל מלווה"
          helperText={helperText}
        />
      )}
    />
  );
}

export function VolunteerOrgContactsFields({
  value,
  onChange,
  description = "תפקיד, שם, טלפון ומייל. טלפון או מייל בלי שם לא יישמרו. אפשר להוסיף אנשי קשר נוספים.",
}: {
  value: VolunteerOrgContactDraft[];
  onChange: (next: VolunteerOrgContactDraft[]) => void;
  description?: string;
}) {
  const updateRow = (
    clientKey: string,
    patch: Partial<VolunteerOrgContactDraft>,
  ) => {
    onChange(
      value.map((row) =>
        row.clientKey === clientKey ? { ...row, ...patch } : row,
      ),
    );
  };

  return (
    <FormSection
      title="אנשי קשר"
      description={description}
    >
      <Stack gap={1.5}>
        {value.map((row) => (
          <Stack key={row.clientKey} gap={1}>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
              <TextField
                size="small"
                label="תפקיד"
                value={row.roleLabel}
                onChange={(event) =>
                  updateRow(row.clientKey, { roleLabel: event.target.value })
                }
                fullWidth
              />
              <TextField
                size="small"
                label="שם"
                value={row.name}
                onChange={(event) =>
                  updateRow(row.clientKey, { name: event.target.value })
                }
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} alignItems="flex-start">
              <TextField
                size="small"
                label="טלפון"
                value={row.phone}
                onChange={(event) =>
                  updateRow(row.clientKey, { phone: event.target.value })
                }
                fullWidth
              />
              <TextField
                size="small"
                label="מייל"
                value={row.email}
                onChange={(event) =>
                  updateRow(row.clientKey, { email: event.target.value })
                }
                fullWidth
              />
              {row.roleKind === "other" ? (
                <Button
                  color="error"
                  onClick={() =>
                    onChange(value.filter((item) => item.clientKey !== row.clientKey))
                  }
                  sx={{ whiteSpace: "nowrap", textTransform: "none", height: 40 }}
                >
                  הסרה
                </Button>
              ) : null}
            </Stack>
          </Stack>
        ))}
        <Button
          variant="outlined"
          onClick={() =>
            onChange([...value, newVolunteerOrgContactDraft(value.length)])
          }
          sx={{ alignSelf: "flex-start", textTransform: "none" }}
        >
          הוסף איש קשר
        </Button>
      </Stack>
    </FormSection>
  );
}

export function VolunteerOrgLocationField({
  value,
  onChange,
  required,
  label,
}: {
  value: VolunteerOrgLocationValue;
  onChange: (next: VolunteerOrgLocationValue) => void;
  required?: boolean;
  label?: string;
}) {
  return (
    <FormGoogle
      size="small"
      label={label ?? (required ? "כתובת" : "כתובת")}
      required={required}
      fullWidth
      placeValue={value.address}
      onLocationChange={({ locationName, latLng }) =>
        onChange({
          address: locationName,
          coordinates: hasValidCoordinates(latLng) ? latLng : null,
        })
      }
      onChange={(event) =>
        onChange({
          address: event.target.value,
          coordinates: value.coordinates,
        })
      }
    />
  );
}

export function VolunteerOrgEventFlagsFields({
  value,
  onChange,
  description = "באילו מצבים אפשר להפעיל. אפשר לסמן כמה.",
  emptyHint = "בלי סימון הארגון לא יופיע בסינון לפי סוג אירוע.",
}: {
  value: VolunteerOrgEventFlags;
  onChange: (next: VolunteerOrgEventFlags) => void;
  description?: string;
  emptyHint?: string;
}) {
  const noneSelected =
    !value.interestedInRoutine &&
    !value.interestedInOutbreak &&
    !value.interestedInEmergency;

  return (
    <FormSection
      title="סוג אירוע"
      description={description}
    >
      <Stack direction="row" gap={1} flexWrap="wrap">
        {VOLUNTEER_ORG_EVENT_TYPES.map((item) => (
          <ChoiceTile
            key={item.id}
            selected={Boolean(value[item.field])}
            label={item.label}
            onClick={() =>
              onChange({ ...value, [item.field]: !value[item.field] })
            }
          />
        ))}
      </Stack>
      {noneSelected ? (
        <Typography color="warning.main" sx={{ fontSize: 13, mt: 1.5 }}>
          {emptyHint}
        </Typography>
      ) : null}
    </FormSection>
  );
}

export function VolunteerOrgDomainsPicker({
  domains,
  selectedIds,
  onChange,
  description,
}: {
  domains: VolunteerOrgActivityDomain[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  description?: string;
}) {
  const queryClient = useQueryClient();
  const { setAlertText, setSeverity } = useAlert();
  const [newName, setNewName] = useState("");
  const selected = domains.filter((domain) => selectedIds.includes(domain.id));
  const options = domains.filter(
    (domain) => domain.isActive || selectedIds.includes(domain.id),
  );

  return (
    <FormSection title="סוג עשייה" description={description}>
      <Autocomplete
        multiple
        size="small"
        options={options}
        value={selected}
        getOptionLabel={(option) => option.name}
        onChange={(_, next) => onChange(next.map((row) => row.id))}
        renderInput={(params) => (
          <TextField {...params} label="תחומים" placeholder="בחירה" />
        )}
      />
      <Stack direction="row" gap={1} sx={{ mt: 1.5 }}>
        <TextField
          size="small"
          label="תחום חדש"
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          fullWidth
        />
        <Button
          variant="outlined"
          disabled={!newName.trim()}
          sx={{ ...quietButtonSx, flexShrink: 0 }}
          onClick={async () => {
            const name = newName.trim();
            if (!name) return;
            try {
              const created = await volunteerOrgActivityDomainRepo.insert({
                name,
                isActive: true,
                sortOrder: domains.length,
              });
              setNewName("");
              await queryClient.invalidateQueries({
                queryKey: ["volunteer-org-activity-domains"],
              });
              onChange([...selectedIds, created.id]);
            } catch (err) {
              setSeverity("error");
              setAlertText(
                err instanceof Error ? err.message : "יצירת תחום נכשלה",
              );
            }
          }}
        >
          הוספה
        </Button>
      </Stack>
    </FormSection>
  );
}
